import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import type { BlockReason } from '../../../services/fuelCards.service';

/** The server's `note` limit (`comdata_card_action.note`). */
const NOTE_MAX = 255;

/**
 * Corpay's own "blocked" code, and the one the operator means nine times out of
 * ten. A blank pick is legal — the server falls back to its configured default —
 * but we default to a real code so the audit row says why the card moved.
 */
const DEFAULT_BLOCK_CODE = 'B';

export interface FuelCardActionModalProps {
  open: boolean;
  action: 'block' | 'unblock';
  /** 1 for a single card, n for a bulk run. */
  cardCount: number;
  /** "J. Smith · Unit 1234 · ****1102" for one card, "3 cards" for a bulk run. */
  cardLabel: string;
  /** From `GET /fuel-cards/block-reasons`. Ignored for unblock. */
  reasons: BlockReason[];
  loading: boolean;
  /** `meta.writes_disabled_reason` — shows a banner and disables Confirm. */
  writesDisabledReason?: string | null;
  onConfirm: (payload: { lock_reason?: string; note?: string }) => void;
  onCancel: () => void;
}

/**
 * Block / unblock confirmation for Fleet Management > Fuel Cards.
 *
 * This modal COLLECTS INTENT AND NOTHING ELSE. It calls `onConfirm` and the page
 * owns the outcome — including the cases only the page can see: `ok: false`,
 * `recorded: false` (Corpay may have acted but our audit/mirror write did not
 * land), a bulk run that stopped early with a `skipped` remainder, and
 * `skipped: true` on a card (an override is in force), which no route the page
 * calls can actually return today and which the page therefore handles
 * defensively — see the page's module doc. So do NOT add a success toast, a "blocked!"
 * message, or any optimistic status flip here: at the moment this component
 * hands off, the action has not happened yet, and when it has, this component is
 * not the one that knows how it went.
 *
 * What it renders is likewise held to what it can know. The bulk copy says the
 * cards go to Comdata one at a time and that a run can stop early, because that
 * is what the endpoint does; it must not read as all-or-nothing. The empty-list
 * copy says reasons are not available *yet* rather than that the fetch failed,
 * because `reasons.length === 0` cannot tell a failure from a fetch still in
 * flight — `loading` here is the action, not the reason lookup.
 */
export const FuelCardActionModal: React.FC<FuelCardActionModalProps> = ({
  open,
  action,
  cardCount,
  cardLabel,
  reasons,
  loading,
  writesDisabledReason,
  onConfirm,
  onCancel,
}) => {
  const isBlock = action === 'block';
  const plural = cardCount > 1;
  const noReasons = isBlock && reasons.length === 0;
  const writesDisabled = Boolean(writesDisabledReason);

  const [lockReason, setLockReason] = useState('');
  const [note, setNote] = useState('');

  // Identity-proof dep: the page may well build this array inline, and a raw
  // `reasons` dependency would then re-run on every render. The separator must
  // be a printable character — a NUL byte here turns the whole FILE binary to
  // git (no line diffs, no blame, take-one-side merges) and invisible to grep.
  const reasonKey = useMemo(() => reasons.map((r) => r.blockCode).join('|'), [reasons]);

  // The note is the operator's own typing, so it resets ONLY on open and on an
  // action flip. A reason list that arrives or changes while the dialog is open
  // (the fetch resolving, a Sync now, a `fallback` -> `comdata` flip) must not
  // throw away what they have already written.
  useEffect(() => {
    if (!open) return;
    setNote('');
  }, [open, action]);

  // The default reason, by contrast, genuinely does depend on the list: 'B' when
  // it is offered (case-insensitively — the codes are whatever Corpay sent),
  // otherwise the first entry.
  useEffect(() => {
    if (!open) return;
    const preferred = reasons.find((r) => r.blockCode.toUpperCase() === DEFAULT_BLOCK_CODE);
    setLockReason(isBlock ? (preferred?.blockCode ?? reasons[0]?.blockCode ?? '') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, reasonKey]);

  // Alt+C cancels, and binds to nothing while a call is in flight — the hook's
  // documented "falsy leaves the chord unbound" idiom, matching the disabled
  // Cancel button. (Stricter than the sibling modals, which bind Alt+C
  // unconditionally: this one cannot abort the request it started, so closing
  // mid-call would only orphan the result.) Confirm gets no chord at all:
  // blocking a driver's fuel card should not be one keystroke away.
  useAltHotkeys({ C: loading ? null : onCancel }, undefined, { layer: open });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const confirmDisabled = loading || writesDisabled || noReasons;

  const handleConfirm = () => {
    if (confirmDisabled) return;
    // Only keys we actually mean. A blank reason and a blank note are both
    // "not specified" to the server, and an empty string says that by accident
    // rather than on purpose — so omit the key instead of sending ''.
    const payload: { lock_reason?: string; note?: string } = {};
    const trimmedReason = lockReason.trim();
    const trimmedNote = note.trim();
    if (isBlock && trimmedReason) payload.lock_reason = trimmedReason;
    if (trimmedNote) payload.note = trimmedNote;
    onConfirm(payload);
  };

  const cardsPhrase = plural ? `${cardCount} cards` : 'card';
  const title = `${isBlock ? 'Block' : 'Unblock'} fuel ${plural ? 'cards' : 'card'}`;
  // While the call is in flight the button says so: a 25-card bulk run against
  // the server's own deadline can hold this dialog for minutes, and a frozen
  // "Block 25 cards" with an unlabelled spinner announces nothing.
  const confirmLabel = loading
    ? `${isBlock ? 'Blocking' : 'Unblocking'} ${cardsPhrase}…`
    : `${isBlock ? 'Block' : 'Unblock'} ${cardsPhrase}`;
  const confirmColor = isBlock
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-green-600 hover:bg-green-700';

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-scrim/50"
        onClick={loading ? undefined : onCancel}
      />
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-busy={loading}
        aria-labelledby="fuel-card-action-title"
        className="relative bg-cmp-modal rounded-lg shadow-xl w-full max-w-md mx-4"
      >
        <ModalHeader
          title={title}
          titleId="fuel-card-action-title"
          onClose={loading ? undefined : onCancel}
          dragHandleProps={dragHandleProps}
        />

        <div className="px-6 py-4 space-y-4">
          {writesDisabled && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
              <span>{writesDisabledReason}</span>
            </div>
          )}

          <div className="text-sm text-label">
            {plural ? (
              <>
                <p>
                  This will {isBlock ? 'block' : 'unblock'}{' '}
                  <span className="font-semibold">{cardLabel}</span>.
                </p>
                {/* The endpoint walks the list serially and stops starting new
                    cards at its own deadline, so this must not read as
                    all-or-nothing. */}
                <p className="mt-2 text-xs text-muted">
                  Each card is sent to Comdata separately; if the run stops early the rest are
                  reported as not attempted. You will get the per-card result when it finishes.
                </p>
              </>
            ) : (
              <p>
                {isBlock ? 'Block' : 'Unblock'} <span className="font-semibold">{cardLabel}</span>?
              </p>
            )}
          </div>

          {isBlock && (
            <div>
              <label
                htmlFor="fuel-card-reason"
                className="block text-sm font-medium text-label mb-1"
              >
                Reason
              </label>
              <select
                id="fuel-card-reason"
                value={lockReason}
                disabled={loading || noReasons}
                // The portal mounts at the end of <body>, so without this a
                // keyboard operator would have to tab through the entire page
                // behind the dialog to reach it.
                autoFocus={!noReasons}
                onChange={(e) => setLockReason(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-body disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700"
              >
                {noReasons ? (
                  <option value="">No reasons available</option>
                ) : (
                  reasons.map((r) => (
                    <option key={r.blockCode} value={r.blockCode}>
                      {r.blockCode} — {r.blockDescription}
                    </option>
                  ))
                )}
              </select>
              {noReasons && (
                // Deliberately "not available yet", not "failed to load": an
                // empty list looks identical whether the fetch failed or is
                // still in flight, and this component is told neither.
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  No block reasons are available yet, so there is nothing to send. Close this and
                  reopen it once the reason list loads.
                </p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="fuel-card-note" className="block text-sm font-medium text-label mb-1">
              Note <span className="text-xs font-normal text-muted">(optional)</span>
            </label>
            <input
              id="fuel-card-note"
              type="text"
              value={note}
              maxLength={NOTE_MAX}
              disabled={loading}
              autoFocus={!isBlock || noReasons}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why, for the audit trail"
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-body disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700"
            />
            <p className="mt-1 text-xs text-muted">
              Will be recorded on the card's action history. {NOTE_MAX} characters maximum.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-label hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="fuel-card-modal-confirm"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white ${confirmColor} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {loading && (
              <div
                className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                aria-hidden="true"
              />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FuelCardActionModal;
